import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';
import {
  EBV_TRAIT_COLUMN_HEADER_HEIGHT,
  categoryCellSx,
  categoryRowEdges,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { EbvTraitHeaderLabel } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import type { EbvTraitHeaderContext } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import TraitLeaderIcon from '@/components/publicSearch/animal/ebv/TraitLeaderIcon';
import { withTraitLeaderCellSx } from '@/components/publicSearch/animal/ebv/traitLeader';
import type { EbvRow } from './animalEbvTabTypes';

type AnimalEbvTabTraitTableProps = {
  isMobile: boolean;
  ebvRows: EbvRow[];
  showPercentileColumn: boolean;
  showAccuracyColumn: boolean;
  /** Left gutter (EBV / Acc / Percentile). Off for dairy values-only (i4 no `heading`). */
  showRowLabels: boolean;
  ebvLabel: string;
  accuracyHeader: ReactNode;
  traitHeaderContext: EbvTraitHeaderContext;
  ebvGroupingOptions?: EbvGroupingOptions;
};

function TraitLeaderValueCell({
  value,
  isTraitLeader,
}: {
  value: string;
  isTraitLeader: boolean;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 28,
        px: isTraitLeader ? 1.25 : 0,
      }}
    >
      {isTraitLeader && (
        <TraitLeaderIcon
          sx={{
            position: 'absolute',
            top: 2,
            right: 0,
          }}
        />
      )}
      {value || '—'}
    </Box>
  );
}

export default function AnimalEbvTabTraitTable({
  isMobile,
  ebvRows,
  showPercentileColumn,
  showAccuracyColumn,
  showRowLabels,
  ebvLabel,
  accuracyHeader,
  traitHeaderContext,
  ebvGroupingOptions,
}: AnimalEbvTabTraitTableProps) {
  return (
    <Table
      size="small"
      sx={{
        minWidth: { xs: 'auto', md: 900 },
        tableLayout: 'fixed',
        '& td:not(:first-of-type)': {
          whiteSpace: 'nowrap',
        },
        '& td:first-of-type': { whiteSpace: 'normal' },
        '& th': {
          whiteSpace: 'normal',
          lineHeight: 1.2,
          verticalAlign: 'bottom',
          minHeight: EBV_TRAIT_COLUMN_HEADER_HEIGHT,
          px: 0.75,
          py: 0.5,
        },
      }}
    >
      {isMobile ? (
        // Pivoted layout for small screens
        <>
          <colgroup>
            <col style={{ width: 140 }} />
            <col style={{ width: 80 }} />
            {showAccuracyColumn && <col style={{ width: 80 }} />}
            {showPercentileColumn && <col style={{ width: 80 }} />}
          </colgroup>
          {(showRowLabels || showAccuracyColumn || showPercentileColumn) && (
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="center">
                  {showRowLabels ? (
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {ebvLabel}
                    </Typography>
                  ) : null}
                </TableCell>
                {showAccuracyColumn && (
                  <TableCell align="center">{accuracyHeader}</TableCell>
                )}
                {showPercentileColumn && (
                  <TableCell align="center">
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Percentile
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {ebvRows.map((ebvRow, i) => {
              // Tint each trait row by its category (top
              // border at the start of each category block)
              // to match the grid / mating predictor.
              const { isFirst } = categoryRowEdges(ebvRows, i);
              const cellSx = categoryCellSx(
                ebvRow.category,
                { top: isFirst },
                ebvGroupingOptions,
              );
              return (
                <TableRow key={ebvRow.key} hover>
                  <TableCell sx={cellSx}>
                    <EbvTraitHeaderLabel
                      label={ebvRow.label}
                      traitKey={ebvRow.traitKey}
                      traitHeaderContext={traitHeaderContext}
                      typographyProps={{ color: 'text.secondary' }}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={withTraitLeaderCellSx(cellSx, ebvRow.isTraitLeader)}
                  >
                    <TraitLeaderValueCell
                      value={ebvRow.value}
                      isTraitLeader={ebvRow.isTraitLeader}
                    />
                  </TableCell>
                  {showAccuracyColumn && (
                    <TableCell align="center" sx={cellSx}>
                      {ebvRow.accuracy || '—'}
                    </TableCell>
                  )}
                  {showPercentileColumn && (
                    <TableCell align="center" sx={cellSx}>
                      {ebvRow.percentile || '—'}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </>
      ) : (
        // Original layout for larger screens
        <>
          <colgroup>
            {showRowLabels && <col style={{ width: 140 }} />}
            {ebvRows.map((ebvRow) => (
              <col key={ebvRow.key} style={{ width: 86 }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {showRowLabels && <TableCell />}
              {ebvRows.map((ebvRow, i) => {
                const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                return (
                  <TableCell
                    key={ebvRow.key}
                    align="center"
                    sx={categoryCellSx(
                      ebvRow.category,
                      {
                        left: isFirst,
                        right: isLast,
                        top: true,
                        header: true,
                      },
                      ebvGroupingOptions,
                    )}
                  >
                    <EbvTraitHeaderLabel
                      label={ebvRow.label}
                      traitKey={ebvRow.traitKey}
                      traitHeaderContext={traitHeaderContext}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              {showRowLabels && (
                <TableCell>
                  <Typography
                    variant="bodysmall"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {ebvLabel}
                  </Typography>
                </TableCell>
              )}
              {ebvRows.map((ebvRow, i) => {
                const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                return (
                  <TableCell
                    key={ebvRow.key}
                    align="center"
                    sx={withTraitLeaderCellSx(
                      categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      ),
                      ebvRow.isTraitLeader,
                    )}
                  >
                    <TraitLeaderValueCell
                      value={ebvRow.value}
                      isTraitLeader={ebvRow.isTraitLeader}
                    />
                  </TableCell>
                );
              })}
            </TableRow>

            {showAccuracyColumn && (
              <TableRow hover>
                {showRowLabels && <TableCell>{accuracyHeader}</TableCell>}
                {ebvRows.map((ebvRow, i) => {
                  const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                  return (
                    <TableCell
                      key={ebvRow.key}
                      align="center"
                      sx={categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      )}
                    >
                      {ebvRow.accuracy || '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {showPercentileColumn && (
              <TableRow hover>
                {showRowLabels && (
                  <TableCell>
                    <Typography
                      variant="bodysmall"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Percentile
                    </Typography>
                  </TableCell>
                )}
                {ebvRows.map((ebvRow, i) => {
                  const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                  return (
                    <TableCell
                      key={ebvRow.key}
                      align="center"
                      sx={categoryCellSx(
                        ebvRow.category,
                        { left: isFirst, right: isLast },
                        ebvGroupingOptions,
                      )}
                    >
                      {ebvRow.percentile || '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </>
      )}
    </Table>
  );
}
